import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { API } from '../../../API';
import Modal from '../../../components/Modal';
import { ExecutionPollResult } from '../types';

import styles from './CancelRunButton.module.scss';

/** What became of a cancel attempt, as far as the caller needs to care. */
export type CancelOutcome =
  /** The backend killed the run and handed back its updated state. */
  | { kind: 'canceled'; execution: ExecutionPollResult }
  /** 409 — the run reached a terminal state first. Benign: just re-sync. */
  | { kind: 'alreadyFinished' };

interface CancelRunButtonProps {
  executionId: string;
  onSettled: (executionId: string, outcome: CancelOutcome) => void;
  className?: string;
}

/** `API.fetch` encodes a 409 as an `Error` carrying `cause.status`. */
function isConflict(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const cause = err.cause as { status?: number } | undefined;
  return cause?.status === 409;
}

/**
 * Cancels a live run, behind a confirmation — an in-flight job is being killed.
 * The endpoint is workspace-scoped, so anyone in the workspace may stop a run,
 * not only whoever started it.
 */
export const CancelRunButton: React.FC<CancelRunButtonProps> = ({
  executionId,
  onSettled,
  className,
}) => {
  const { t } = useTranslation();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);

  const closeConfirm = useCallback(() => {
    if (isCanceling) return;
    setIsConfirmOpen(false);
  }, [isCanceling]);

  const handleConfirm = useCallback(async () => {
    if (isCanceling) return;
    setIsCanceling(true);
    try {
      const execution: ExecutionPollResult = await API.fetch(
        'POST',
        `/API/v2/windmill/executions/${executionId}/cancel`,
      );
      toast.success(t('Run canceled') as string);
      setIsConfirmOpen(false);
      onSettled(executionId, { kind: 'canceled', execution });
    } catch (err: unknown) {
      if (isConflict(err)) {
        // The run finished while the user was clicking. Nothing went wrong —
        // tell them so and let the caller pull the real state.
        toast.info(t('This run already finished.') as string);
        setIsConfirmOpen(false);
        onSettled(executionId, { kind: 'alreadyFinished' });
      } else {
        // 404 / 502 / anything else: a localised generic message. Customer
        // facing responses deliberately carry no raw error detail.
        toast.error(t('Could not cancel the run. Please try again.') as string);
      }
    } finally {
      setIsCanceling(false);
    }
  }, [executionId, isCanceling, onSettled, t]);

  return (
    <>
      <button
        type="button"
        className={className ?? styles.cancelButton}
        disabled={isCanceling}
        // The control can sit inside a clickable run-history row.
        onClick={(e) => {
          e.stopPropagation();
          setIsConfirmOpen(true);
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {isCanceling ? t('Canceling...') : t('Cancel run')}
      </button>

      {isConfirmOpen && (
        <Modal
          isVisible={isConfirmOpen}
          title={t('Cancel run')}
          content={t(
            'Are you sure you want to cancel this run? The job will be stopped.',
          )}
          footer={
            <div className={styles.modalFooter}>
              <button
                type="button"
                className={styles.modalDismiss}
                onClick={closeConfirm}
                disabled={isCanceling}
              >
                {t('Keep running')}
              </button>
              <button
                type="button"
                className={styles.modalConfirm}
                onClick={handleConfirm}
                disabled={isCanceling}
              >
                {isCanceling ? t('Canceling...') : t('Yes, cancel')}
              </button>
            </div>
          }
          onClose={closeConfirm}
          className={undefined}
        />
      )}
    </>
  );
};
